import './myseasons.css'

import { trashOutline, settingsOutline, cloudDoneOutline, add } from 'ionicons/icons'
import {
	useIonAlert,
	IonButton,
	IonIcon,
	IonLabel,
} from '@ionic/react'
import { App } from '@capacitor/app'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { GameField } from './Games'
import { Warning } from './Roster'
import {
  useActiveTab,
  useAmViewingOwnTeam,
  useCanEditItem,
  useDeleteItem,
  useHaveValidSeason,
  useMyGames,
  useMyNumPeriods,
  useMyRosters,
  useMySeason,
  useMyTeam,
  useSetAmAddingNewSeason,
  useSetNewTeamId,
  useSetShowTeamSelection,
  useShouldSyncFixtureState,
  useTeamSeasons,
  useUser,
} from './data'
import { ucFirst } from './util'
import { useDisableSync, useImportChanges, useLockGame, useRemoveSkipMatchId, useSaveItem, useSkipFixtureMatch, useSyncFixture, useUnlockGame } from './functions'
import EditButton from './EditButton'
import List from './List'
import ModalWithCancel from './ModalWithCancel'
import { SeasonEditor } from './SeasonEditor'
import { sortFixtureChanges } from './fixtureSync'
import {
	SyncManagement,
	SyncManagementItem,
} from './SyncManagement'

const type: ItemTypeDefinition<Season> = {
	name: 'seasons',
	unit: 'season',

	// If you add something here, make sure you give it a defaultValue below!!
	fields: [ 'name', 'gameFormat', 'numPeriods', 'privacy', 'rules', 'connector' ],

	optionalFields: [ 'connector' ],
	defaultValue: ({ field }) => {
		if (field === 'numPeriods') {
			return 4
		}
		if (field === 'gameFormat') {
			return 7
		}
		if (field === 'privacy') {
			return false
		}
		if (field === 'rules') {
			return 0
		}
		return undefined
	},
	disableFields: ({ field, mode, isSeasonSettings }) => {
		if (field === 'connector' && !isSeasonSettings && mode === "edit") {
			return true
		}
		return false
	},
	pathname: '/games',
	maxWithoutPro: 1,
	formatField: props => <SeasonField {...props} />,
	// List newer seasons first
	sortFunction: (a, b) => ((b.created || 0) - (a.created || 0)) || String(a.name).localeCompare(b.name),
}

type MySeasonsProps = {
	teamId: TeamId
}

const MySeasons = (props: MySeasonsProps) => {

    const seasons = useTeamSeasons(props.teamId)

	return (
		<List
            data={seasons}
			type={type}
		/>
	)
}

type SeasonFieldProps = {
	field: keyof Season
	value: Season[keyof Season]
}

const SeasonField = (props: SeasonFieldProps) => {

	if (props.field === 'name') {
		return (
			<>
				{props.value as Season['name']}
			</>
		)
	}

	return null
}

type EditSeasonProps = {
	seasonEditMode: boolean
	setSeasonEditMode: (v: boolean) => void
	loading: boolean
	scrollTargetRef?: React.RefObject<HTMLElement | null>
}

//
// Provide a button or edit field at the bottom of the Games list
//
export const EditSeason = (props: EditSeasonProps) => {

	const { seasonEditMode, setSeasonEditMode, loading, scrollTargetRef } = props

	const canEdit = useCanEditItem('season')
	const season = useMySeason()
	const team = useMyTeam()
	const user = useUser()
	const deleteSeason = useDeleteItem<Season>()
	const haveValidSeason = useHaveValidSeason()
	const saveItem = useSaveItem<Season>()
	const [ presentAlert ] = useIonAlert()

	if (!loading && !haveValidSeason) {
		return <Warning>This season has been deleted.</Warning>
	}

	if (!canEdit || !season)
		return null

	if (!seasonEditMode) {

		const icon = season.connector ? cloudDoneOutline : settingsOutline

		return (
			<EditButton
				className="season-edit-button"
				text="Season settings"
				icon={icon}
				show={true}
				ready={true}
				editMode={true}
				onClick={() => setSeasonEditMode(true)}
			/>
			)
	}

	const saveSeasonPatch = async (patch: Partial<Season> & { id: SeasonId }) => {
		try {
			await saveItem('season', patch)
		} catch (err) {
			console.error("Failed to update season", err)
			presentAlert({
				header: 'Unable to Save',
				message: 'Season settings could not be updated. Please try again.',
				buttons: [ 'OK' ],
			})
			throw err
		}
	}

	const presentDeleteError = (err: unknown) => {
		console.error("Failed to delete season", err)
		presentAlert({
			header: 'Unable to Delete',
			message: 'This season could not be deleted. Please check your connection and try again.',
			buttons: [ 'OK' ],
		})
	}

	const confirmDeleteSeason = async () => {
		try {
			const alertData = await deleteSeason({
				type,
				item: season,
				onError: presentDeleteError,
				ownedByTeamId: team?.id,
				parentName: team?.name,
				onViewSeason: () => setSeasonEditMode(false),
			})
			presentAlert(alertData)
			setSeasonEditMode(false)
		} catch (err) {
			presentDeleteError(err)
		}
	}

	const onClickDeleteSeason = () => {
		const shouldWarnCoCoach = !!user && !!team?.managers?.includes(user.uid) && team.managers.length > 1

		if (!shouldWarnCoCoach) {
			confirmDeleteSeason()
			return
		}

		presentAlert({
			header: 'Co-Coach Warning',
			message: "Removing data deletes it for all of this team's coaches, not just you. Do you want to proceed?",
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Continue',
					handler: confirmDeleteSeason,
				},
			],
		})
	}

	return (
		<div className="season-edit-container">
			<SeasonEditor
				mode="edit"
				season={season}
				onSavePatch={saveSeasonPatch}
				connectorMode="current"
				className="edit-season-settings"
				scrollTargetRef={scrollTargetRef}
				onClose={() => setSeasonEditMode(false)}
			/>
			<div className="season-edit-delete-button">
				<IonButton
					className="item-menu-item item-menu-delete"
					onClick={onClickDeleteSeason}
					color="danger"
					fill="clear"
					size="small"
				>
					<IonIcon icon={trashOutline} slot="start" />
					<IonLabel>
						Delete Season
					</IonLabel>
				</IonButton>
			</div>
			<AddNewSeasonForCurrentTeam />
		</div>
		)
}

export const SyncConnector = () => {

	const season = useMySeason()
	const numPeriods = useMyNumPeriods()

	const activeTab = useActiveTab()

	const amViewingOwnTeam = useAmViewingOwnTeam()

	const syncFixture = useSyncFixture()
	const disableSync = useDisableSync()
	const importChanges = useImportChanges()
	const lockGame = useLockGame()
	const unlockGame = useUnlockGame()
	const skipFixtureMatch = useSkipFixtureMatch()
	const removeSkipMatchId = useRemoveSkipMatchId()

	const [ shouldSyncFixture, setShouldSyncFixture ] = useShouldSyncFixtureState()

	const [ syncResult, setSyncResult ] = useState<SyncResult|null|undefined>()
	const [ userInitiatedSync, setUserInitiatedSync ] = useState(false)
	const [ isImportingChanges, setIsImportingChanges ] = useState(false)
	const isImportingChangesRef = useRef(false)

	const connector: Connector|undefined = season?.connector

	const lastCheckedTimeRef = useRef<number>()
	const lastCheckedKeyRef = useRef<string>()
	const isSyncingRef = useRef(false)

	const games = useMyGames()

	const [ presentAlert ] = useIonAlert()

	const changes = syncResult?.changes

	const MIN_BETWEEN_FETCHES = 5 * 60 * 1000		// 5 minutes

	const syncContextKey = connector ? [
		season?.id || 'no-season',
		connector.type,
		String(connector.params?.competitionId || 'no-competition'),
		String(connector.params?.id || connector.id),
	].join(':') : undefined

	const requestAutoSync = useCallback((source: string, requireGamesTab: boolean) => {
		if (!connector || !syncContextKey || !amViewingOwnTeam) {
			return
		}
		if (requireGamesTab && activeTab !== 'games') {
			return
		}
		if (isSyncingRef.current || shouldSyncFixture) {
			return
		}

		const now = Date.now()
		if (
			lastCheckedKeyRef.current !== syncContextKey ||
			!lastCheckedTimeRef.current ||
			now - lastCheckedTimeRef.current > MIN_BETWEEN_FETCHES
		) {
			console.log('SYNC', source)
			lastCheckedKeyRef.current = syncContextKey
			lastCheckedTimeRef.current = now
			setShouldSyncFixture('auto')
		} else {
			console.log('NO SYNC', source)
		}
	}, [ connector, syncContextKey, amViewingOwnTeam, activeTab, shouldSyncFixture, setShouldSyncFixture, MIN_BETWEEN_FETCHES ])

	useEffect(() => {

		const timer = setTimeout(() => {
			requestAutoSync('games-tab', true)
		}, 2000)

		return () => clearTimeout(timer)
	}, [ requestAutoSync ])

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				requestAutoSync('visibilitychange', false)
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)

		const appStateListener = App.addListener('appStateChange', ({ isActive }) => {
			if (isActive) {
				requestAutoSync('appStateChange', false)
			}
		})

		const resumeListener = App.addListener('resume', () => {
			requestAutoSync('resume', false)
		})

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			void appStateListener.then(handle => handle.remove())
			void resumeListener.then(handle => handle.remove())
		}
	}, [ requestAutoSync ])

	useEffect(() => {
		async function doSyncFixture(isUserInitiated: boolean) {
			if (connector) {
				isSyncingRef.current = true
				try {
					const syncMode: FixtureSyncMode = isUserInitiated ? 'comprehensive' : 'quick'
					const data = await syncFixture(connector, numPeriods, syncMode)
					if (data) {
						setSyncResult(data)
						setUserInitiatedSync(isUserInitiated)
					} else if (isUserInitiated) {
						setSyncResult(null)
					}
				} finally {
					isSyncingRef.current = false
				}
			}
		}

		if (shouldSyncFixture) {
			const isUserInitiated = shouldSyncFixture === 'user'
			setShouldSyncFixture(false)
			if (!amViewingOwnTeam) {
				setSyncResult(undefined)
				setUserInitiatedSync(false)
				return
			}
			if (syncContextKey) {
				lastCheckedKeyRef.current = syncContextKey
				lastCheckedTimeRef.current = Date.now()
			}
			void doSyncFixture(isUserInitiated)
		}
	}, [ shouldSyncFixture, connector, numPeriods, setShouldSyncFixture, syncFixture, syncContextKey, amViewingOwnTeam ])

	if (!connector)
		return null

	const icons = {
		add: '🟢',
		delete: '🔴',
		modify: '🟡',
	} as const

	const changesSummary = {
		add: 0,
		delete: 0,
		modify: 0,
	}

	changes?.forEach(change => {
		let type: keyof typeof changesSummary
		if ('add'in change) {
			type = 'add'
		} else if ('delete' in change) {
			type = 'delete'
		} else {
			type = 'modify'
		}
		changesSummary[type] += 1
	})

	const changesSummaryList = Object.entries(changesSummary)
			.filter(([ _key, value ]) => value > 0)
			.map(( [ key, value ]) => `${icons[key as keyof typeof icons]} ${ucFirst(key)} ${value} game${value === 1 ? '' : 's'}`)

	const onImportChanges = async () => {
		if (!changes || isImportingChangesRef.current) {
			return
		}

		isImportingChangesRef.current = true
		setIsImportingChanges(true)

		try {
			const numErrors = await importChanges(changes)
			if (numErrors) {
				presentAlert({
					header: 'Import Incomplete',
					message: "Not all changes were able to be imported.",
					buttons: [ 'OK' ],
				})
			} else {
				setSyncResult(undefined)
			}
		} catch (err) {
			console.error("Failed to import fixture changes", err)
			presentAlert({
				header: 'Unable to Import',
				message: "Import failed unexpectedly. Please try again.",
				buttons: [ 'OK' ],
			})
		} finally {
			isImportingChangesRef.current = false
			setIsImportingChanges(false)
		}
	}

	const clickImportAll = () => {

		if (changesSummary.delete) {
			presentAlert({
				header: 'WARNING: Really delete?',
				message: `This import will DELETE one or more of your games, including any associated rosters.`,
				buttons: [
					{
						text: 'Cancel',
						role: 'cancel',
					},
					{
						text: 'Accept Changes',
						handler: () => {
							void onImportChanges()
						},
					},
				],
			})
		} else {
			void onImportChanges()
		}
	}

	const clickDisableSync = () => {
		presentAlert({
			header: 'Disconnecting',
			message: "Your fixture will no longer auto-update. You can re-enable syncing via your Season settings.",
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Stop Syncing',
					handler: disableSync,
				},
			],
		})
	}

	const handleSkip = async (change: ChangeAddGame | ChangeDeleteGame | ChangeUpdateGame) => {
		try {
			if ('add' in change) {
				if (change.matchId != null) {
					await skipFixtureMatch(change.matchId)
					setSyncResult(prev => {
						if (!prev) return prev
						return {
							...prev,
							changes: prev.changes.filter(c => c !== change),
							suppressedAdds: [
								...prev.suppressedAdds,
								{
									matchId: change.matchId!,
									game: { ...change.game },
								},
							],
						}
					})
				}
			} else if ('delete' in change) {
				await lockGame(change.delete)
				setSyncResult(prev => {
					if (!prev) return prev
					return {
						...prev,
						changes: prev.changes.filter(c => c !== change),
						suppressedGames: [ ...prev.suppressedGames, { game: change.game, wouldDelete: true } ],
					}
				})
			} else {
				await lockGame(change.myGameId)
				const game = games?.[change.myGameId]
				setSyncResult(prev => {
					if (!prev) return prev
					return {
						...prev,
						changes: prev.changes.filter(c => c !== change),
						suppressedGames: game ? [ ...prev.suppressedGames, { game, wouldDelete: false, wouldUpdateChanges: change.changes } ] : prev.suppressedGames,
					}
				})
			}
		} catch (err) {
			console.error('Skip failed', err)
			presentAlert({
				header: 'Skip Failed',
				message: 'Unable to skip this change. Please try again.',
				buttons: [ 'OK' ],
			})
		}
	}

	const handleUnlock = async (gameId: GameId) => {
		await unlockGame(gameId)
		setSyncResult(prev => {
			if (!prev) return prev
			const suppressed = prev.suppressedGames.find(s => s.game.id === gameId)
			const remaining = { ...prev, suppressedGames: prev.suppressedGames.filter(s => s.game.id !== gameId) }
			if (!suppressed) return remaining
			if (suppressed.wouldDelete) {
				return { ...remaining, changes: sortFixtureChanges([...prev.changes, { delete: suppressed.game.id, game: suppressed.game }], games) }
			}
			if (suppressed.wouldUpdateChanges?.length) {
				return { ...remaining, changes: sortFixtureChanges([...prev.changes, { myGameId: suppressed.game.id, changes: suppressed.wouldUpdateChanges }], games) }
			}
			return remaining
		})
	}

	const handleUnignore = async (matchId: ConnectorMatchId) => {
		await removeSkipMatchId(matchId)
		setSyncResult(prev => {
			if (!prev) return prev
			const suppressed = prev.suppressedAdds.find(s => s.matchId === matchId)
			const remaining = { ...prev, suppressedAdds: prev.suppressedAdds.filter(s => s.matchId !== matchId) }
			if (!suppressed) return remaining
			const restoredChange: ChangeAddGame = {
				add: true,
				game: { ...suppressed.game, created: Date.now() },
				matchId: suppressed.matchId,
			}
			return { ...remaining, changes: sortFixtureChanges([...prev.changes, restoredChange], games) }
		})
	}

	const clickSkip = (change: ChangeAddGame | ChangeDeleteGame | ChangeUpdateGame) => {
		presentAlert({
			header: 'Skip sync for this game?',
			message: "This will permanently ignore official fixture changes for this game. You can change this later.",
			buttons: [
				{ text: 'Cancel', role: 'cancel' },
				{ text: 'Skip game', handler: () => void handleSkip(change) },
			],
		})
	}

	return (
		<ModalWithCancel
			show={amViewingOwnTeam && connector && syncResult !== undefined && (syncResult === null || !!changes?.length || userInitiatedSync)}
			setShow={() => {
				if (!isImportingChangesRef.current) {
					setSyncResult(undefined)
				}
			}}
		>
			<div className="fixture-sync-modal">
				<h1>
					Season Sync (Beta) <IonIcon icon={cloudDoneOutline} />
				</h1>
				<p>
					{syncResult === null
						? <>Unable to check fixture for <b>{connector.params.teamName} ({connector.params.compName})</b>. Please try again later.</>
						: changes?.length
							? <>Found updated fixture data for <b>{connector.params.teamName} ({connector.params.compName})</b>. Import these changes?</>
							: <>Your fixture is up to date for <b>{connector.params.teamName} ({connector.params.compName})</b>.</>
					}
				</p>
				<DisplayFixtureChanges
					changes={changes}
					suppressedGames={syncResult?.suppressedGames ?? []}
					suppressedAdds={syncResult?.suppressedAdds ?? []}
					onSkip={clickSkip}
					onUnlock={handleUnlock}
					onUnignore={handleUnignore}
				/>
				<div className="sync-connector-buttons">
					<IonButton
						expand="block"
						onClick={clickImportAll}
				style={{display: changes?.length ? undefined : 'none'}}
						disabled={isImportingChanges}
					>
						<div className="sync-connector-accept-button">
							<div className="sync-connector-change-text">
								Accept Changes:
							</div>
							<div className="sync-connector-change-list">
								{
									changesSummaryList && changesSummaryList.map((str, index) => (
										<div key={index}>
											{str}
										</div>
									))
								}
							</div>
						</div>

					</IonButton>
					<IonButton
						color="danger"
						fill="clear"
						onClick={clickDisableSync}
						disabled={isImportingChanges}
					>
						Stop Syncing
					</IonButton>
				</div>
			</div>
		</ModalWithCancel>
	)
}

type DisplayFixtureChangesProps = {
	changes: Changes | undefined
	suppressedGames?: SuppressedGame[]
	suppressedAdds?: SuppressedAdd[]
	onSkip?: (change: ChangeAddGame | ChangeDeleteGame | ChangeUpdateGame) => void
	onUnlock?: (gameId: GameId) => void
	onUnignore?: (matchId: ConnectorMatchId) => void
}

const DisplayFixtureChanges = (props: DisplayFixtureChangesProps) => {

	const { changes, suppressedGames = [], suppressedAdds = [], onSkip, onUnlock, onUnignore } = props

	const games = useMyGames()

	if (!changes || !games)
		return null

	const numGames = Object.keys(games).length
	const modifications = changes.filter(change => !('add' in change)) as Array<ChangeDeleteGame | ChangeUpdateGame>
	const numModifications = modifications.length
	const isChangingEveryGame = numModifications === numGames
	const now = Date.now()
	const numModificationsToPastGames = modifications.filter(change => {
		const gameId = 'myGameId' in change ? change.myGameId : change.game.id
		const game = games[gameId]
		if (!game) {
			return false
		}
		// Ignore result-only changes, as those are typical post-game score updates that
		// don't require a warning.
		if ('changes' in change && change.changes.length === 1 && change.changes[0].field === 'result') {
			return false
		}
		return game.date < now
	}).length
	const syncManagementItems: SyncManagementItem[] = [
		...suppressedGames.map(s => ({
			date: s.game.date,
			round: s.game.round,
			key: `locked-${s.game.id}`,
			label: `Round ${s.game.round} vs ${s.game.opponent} ${s.wouldDelete ? 'is not in official fixture' : '— fixture has changes'}`,
			onAction: onUnlock ? () => onUnlock(s.game.id) : undefined,
			actionLabel: 'Allow sync',
		})),
		...suppressedAdds.map(s => ({
			date: s.game.date,
			round: s.game.round,
			key: `skipped-${s.matchId}`,
			label: `Round ${s.game.round} vs ${s.game.opponent} (added to official fixture)`,
			onAction: onUnignore ? () => onUnignore(s.matchId) : undefined,
			actionLabel: 'Allow sync',
		})),
	]

	return (
		<div className="connector-fixture-changes">
			{
				changes.map((changedGame, index) => {
					if ('delete' in changedGame) {
						return (
							<div
								key={index}
								className="autoroster-item game connector-item-delete-game"
							>
								<div className="connector-change-header connector-change-header-delete">
									<div className="connector-delete-game">
										Game Not Found
									</div>
									{onSkip && (
										<IonButton
											fill="clear"
											color="light"
											size="small"
											className="connector-skip-button"
											onClick={() => onSkip(changedGame)}
										>
											Skip...
										</IonButton>
									)}
								</div>
								<DisplayGame
									game={changedGame.game}
									delete={changedGame.delete}
									isFixtureChange={true}
								/>
							</div>
						)
					}

					let data: Array<FieldChange>
					let game

					if ('changes' in changedGame) {
						data = changedGame.changes
						game = games[changedGame.myGameId]

					} else {
						// For added games, don't display 'id', 'created' - only content we care about
						const noDisplay = [ 'id', 'created', 'round' ]
						data = Object.entries(changedGame.game).filter(([ field, _value ]) => !noDisplay.includes(field)).map(([ field, newValue ]) => ({
							field: field as keyof Game,
							newValue,
							oldValue: undefined,
						}))
						game = changedGame.game
					}

					let content = (
						<>
							{
								data.map(change => (
									<div
										key={change.field}
										className="connector-change"
									>
										<div className="connector-change-label">
											New {change.field}
										</div>
										<div
											className={`connector-change-field`}
										>
											<GameField
												field={change.field}
												value={change.newValue}
												isFixtureChange={true}
											/>
										</div>
									</div>
								))
							}
						</>
					)

					if ('add' in changedGame) {
						return (
							<div
								key={index}
								className="autoroster-item game connector-item-add-game"
							>
								<div className="connector-change-header connector-change-header-add">
									<div className="connector-add-game">
										New Game Found
									</div>
									{onSkip && changedGame.matchId != null && (
										<IonButton
											fill="clear"
											color="light"
											size="small"
											className="connector-skip-button"
											onClick={() => onSkip(changedGame)}
										>
											Skip...
										</IonButton>
									)}
								</div>
								<div className="field-round">
									<GameField
										field="round"
										value={game.round}
									/>
								</div>
								{content}
							</div>
						)
					}

					return (
						<div
							className="autoroster-item game connector-item-modify-game"
							key={changedGame.myGameId}
						>
							<div className="connector-change-header">
								<div className="connector-add-game">
									Change Found
								</div>
								{onSkip && (
									<IonButton
										fill="clear"
										color="light"
										size="small"
										className="connector-skip-button"
										onClick={() => onSkip(changedGame)}
									>
										Skip...
									</IonButton>
								)}
							</div>
							<DisplayGame
								game={game}
								isFixtureChange={true}
							/>
							{content}
						</div>
					)
				})
			}
			<SyncManagement items={syncManagementItems} />
			{
				changes.length ? (
					<p className="warning">
						Changes cannot be undone. Please check carefully before accepting.
					</p>
				) : null
			}
			{
				numModificationsToPastGames ? (
					<div className="warning connector-modify-game-warning">
						⚠️  This includes changes to <b>{numModificationsToPastGames} past game{numModificationsToPastGames === 1 ? '':'s'}</b>.
						{
							isChangingEveryGame ? (
								<>
									<br /><br />
									⚠️  This will change <b>every</b> game in your season!
								</>
							) : null
						}
					</div>
				) : null
			}
		</div>
	)
}

type DisplayGameProps = {
	game: Game | Omit<Game, 'id'>
	delete?: string
	isFixtureChange?: boolean
}

const DisplayGame = (props: DisplayGameProps) => {

	const { game } = props

	const rosters = useMyRosters()

	if (!game)
		return null

	const fields = [ 'round', 'opponent', 'date', 'venue', 'result' ] as const

	return (
		<div className="item-content">
			{
				fields.map(field => (
					<div
						key={field}
						className={`field-${field}`}
					>
						<GameField
							field={field}
							value={game[field]}
							item={{
								...game,
								id: ('id' in game ? game.id : 'temp'),
							}}
							setEditMode={() => null}
							isFixtureChange={props.isFixtureChange}
						/>
					</div>
				))
			}
			{
				props.delete ? (
					<div className="connector-delete-game-warning">
						<span>
							⚠️  This game doesn't exist in the official fixture. Sync will delete it from your Games.
						</span>
					</div>
				) : null
			}
			{
				(props.delete && props.game.result) ? (
					<div className="connector-delete-game-warning">
						<span>
							⚠️  Scores will be lost.
						</span>
					</div>
				) : null
			}
			{
				(props.delete && rosters[props.delete]) ? (
					<div className="connector-delete-game-warning">
						<span>
							⚠️  Roster will be lost.
						</span>
					</div>
				) : null
			}
		</div>
	)
}

export const AddNewSeasonForCurrentTeam = () => {
	const team = useMyTeam()
	const setNewTeamId = useSetNewTeamId()
	const setShowTeamSelection = useSetShowTeamSelection()
	const setAmAddingNewSeason = useSetAmAddingNewSeason()

	if (!team)
		return null

	return (
		<div className="season-edit-add-button">
			<IonButton
				className="item-menu-item item-menu-delete"
				onClick={() => {
					console.log('add new season for current team')
					setNewTeamId(team.id)
					setShowTeamSelection(true)
					setAmAddingNewSeason(true)
				}}
				fill="clear"
				size="small"
			>
				<IonIcon icon={add} slot="start" />
				<IonLabel>
					Add New Season for {team.name}
				</IonLabel>
			</IonButton>
		</div>
	)
}

export default MySeasons
